/**
 * SOLID Review
 * S — Strong. One concern: the session's use cases and the collaborators they share, built once.
 * O — Good. A new session op is one use case built here; a new way into the game one step in the entry route.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Exactly the session's ops of `ClientUseCases`.
 * D — Good. Port interfaces, the account's sign-out and the shared steps, injected; what it builds is held by role
 *     (`IUseCase`).
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { ICommandStartSession, ICommandCreateGuest, ICommandTableCode, ICommandNavigate } from '../../domain/ports/schemas/SessionMoves';
import type {
  IPortClientTabStorage, IPortClientRoomServer, IPortClientRoundServer, IPortClientPlayerServer, IPortClientAuthServer,
} from '../ports/contracts';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { ClientSteps } from '../shared/ClientSteps';
import { ServerRoundTrip } from '../shared/ServerRoundTrip';
import { DecisionStep } from '../shared/DecisionStep';
import type { IConfigDepSession } from './IConfigDepSession';
import { StoredRecords } from './StoredRecords';
import { SeatRestore } from './SeatRestore';
import { SessionEntryRoute, EntryEnds, type TEntryRoute } from './SessionEntryRoute';
import { JoinByCodeStep } from './JoinByCodeStep';
import { ColdRejoinStep } from './ColdRejoinStep';
import { HostTableStep } from './HostTableStep';
import { StartSessionUseCase, type IConfigDepStartSession } from './StartSessionUseCase';
import { EnterGameUseCase, type IConfigDepEnterGame } from './EnterGameUseCase';
import { CreateGuestUseCase, type IConfigDepCreateGuest } from './CreateGuestUseCase';
import { CheckTableCodeUseCase } from './CheckTableCodeUseCase';

export interface IConfigDepSessionContext {
  readonly decisions: IPortClientDecisions;
  readonly tabStorage: IPortClientTabStorage;
  readonly rooms: IPortClientRoomServer;
  readonly rounds: IPortClientRoundServer;
  readonly players: IPortClientPlayerServer;
  readonly auth: IPortClientAuthServer;
  /** The account's sign-out use case (FE3), shared with the Log out buttons. */
  readonly account: IUseCase<ICommandNothing, TPortOutcome>;
  /** The one serial section, shared with the table. */
  readonly steps: ClientSteps;
}

/**
 * THE SESSION (frontend strict-ports plan, FE2, 2026-10-03; use cases since frontend-application-oo P3). What were four hooks
 * watching the store from AppRoot — identity bootstrap, session recovery, cold-login rejoin, smart-join routing — plus
 * MainMenuPanel's auto-host and redirect, IdentityModal's submit and EnterCodeScreen's check, are use cases of FE1's pattern
 * on the shared serial section: a step is snapshot → decide → save (→ keep), and a server call sits between two steps.
 */
export class SessionContext {
  /** The page loaded: hydrate's identity and screen, and what the URL asked for. */
  readonly startSession: IUseCase<ICommandStartSession, TPortOutcome>;
  /** The game screen mounted: identity, the stored game, then the code, the cold rejoin, the host — or back. */
  readonly enterGame: IUseCase<ICommandNothing, TPortOutcome>;
  /** The name modal's submit: create the guest, then go on with the entry. */
  readonly createGuest: IUseCase<ICommandCreateGuest, TPortOutcome>;
  /** EnterCodeScreen's Continue. */
  readonly checkTableCode: IUseCase<ICommandTableCode, TPortOutcome>;
  /** A funnel button. */
  readonly navigate: IUseCase<ICommandNavigate, TPortOutcome>;
  /** The welcome screen's ✕ on a join error. */
  readonly dismissJoinError: IUseCase<ICommandNothing, TPortOutcome>;

  constructor(deps: IConfigDepSessionContext) {
    const { steps, decisions } = deps;
    const session: IConfigDepSession = { ...deps, step: steps.onSession, trip: new ServerRoundTrip<IPropsClientInput>(steps.onSession) };
    const records = new StoredRecords(deps.tabStorage);
    const seats = new SeatRestore(session);
    const ends = new EntryEnds();
    const routes: TEntryRoute = {
      join: new JoinByCodeStep(session, seats), cold: new ColdRejoinStep(session, seats), host: new HostTableStep(session),
      seated: ends, idle: ends, wait: ends,
    };
    const route = new SessionEntryRoute(session, routes);
    const starting: IConfigDepStartSession = { ...session, records };
    this.startSession = new StartSessionUseCase(starting);
    const entering: IConfigDepEnterGame = { ...session, records, seats, route };
    this.enterGame = new EnterGameUseCase(entering);
    const naming: IConfigDepCreateGuest = { ...session, route };
    this.createGuest = new CreateGuestUseCase(naming);
    this.checkTableCode = new CheckTableCodeUseCase(session);
    this.navigate = new DecisionStep<IPropsClientInput, ICommandNavigate>(steps.onSession, (ctx, input, command) => decisions.navigate(ctx, input, command));
    this.dismissJoinError = new DecisionStep<IPropsClientInput, ICommandNothing>(steps.onSession, (ctx, input, command) => decisions.dismissJoinError(ctx, input, command));
  }
}
